import React, { useState } from 'react';
import cn from 'classnames';
import _ from 'lodash';

import {
  useTranslation,
  useResponsive,
  useAppSelector,
  useWaitingList,
  useBlockedList,
  useAppDispatch,
  useGAEventCommonProps,
} from 'client/hooks';
import { selectRoomId, selectRoomStatus } from 'client/redux/room/selectors';
import { PB_CHANNELS } from 'client/redux/pubnub/constants';
import { getRaiseHandMessages, getBlockedUsers, getAllUsersMetadata } from 'client/redux/pubnub/actions';
import {
  selectListenersAllowedToSpeak,
  selectRaiseHandUsersTotal,
  selectSpokeUsersMetaData,
} from 'client/redux/pubnub/selectors';
import { USER_ROLES, ROOM_STATUS } from 'constants/index';
import { PubnubMsgsItem, UserMetadata } from 'client/redux/pubnub/types';
import { analytics } from 'client/utils/analytics';

import WaiterSpeakContainer from '../../RoomContent/WaiterSpeakContainer';
import GeneralStatisticsList from '../../GeneralStatisticsList';
import TransitionComponent from '../../TransitionComponent';
import { DrawerCloseBtn } from '../DrawerCloseBtn';
import VirtualUsersList from './VirtualUsersList';

import Button from '../../Button';

import css from './ListenersDetailsDrawer.module.scss';

interface Props {
  className?: string;
  totalListenersCount?: number;
  connectedListenersCount?: number;
  pendingRequestsCount?: number;
  visible: boolean;
  userRole: USER_ROLES;
  handleClose: () => void;
}

const ListenersDetailsDrawer: React.FC<Props> = (props) => {
  const {
    className = '',
    visible,
    totalListenersCount = 0,
    connectedListenersCount = 0,
    pendingRequestsCount = 0,
    userRole,
    handleClose,
  } = props;
  const { translate } = useTranslation('actionsPanel');
  const listOfAllowedToSpeak = useAppSelector(selectListenersAllowedToSpeak);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [isMobile] = useResponsive('MOBILE');
  const dispatch = useAppDispatch();
  const roomId = useAppSelector(selectRoomId);
  const { common } = useGAEventCommonProps();
  const [isShowStatistic, setIsShowStatistic] = useState<boolean>(false);
  const waitingList = useWaitingList();
  const { blockedList, blockedListTotal } = useBlockedList();
  const isListenerTalking = listOfAllowedToSpeak.length > 0;
  const waitingListTotal = useAppSelector(selectRaiseHandUsersTotal);
  const spokeUsersMetaData = useAppSelector(selectSpokeUsersMetaData);
  const roomStatus = useAppSelector(selectRoomStatus);
  const isRoomFinished = roomStatus == ROOM_STATUS.FINISHED;

  const [isShowMoreBtn, setIsShowMoreBtn] = useState<boolean>(false);

  const TABS = React.useMemo(
    () => [
      {
        index: '0',
        title: translate('listenerDetailsDrawer.tabs.speechRequests'),
        count: waitingListTotal,
        disabled: false,
      },
      {
        index: '1',
        title: translate('listenerDetailsDrawer.tabs.recievedRequests'),
        count: spokeUsersMetaData.totalCount,
        disabled: spokeUsersMetaData.totalCount > 0 ? false : true,
      },
      {
        index: '2',
        title: translate('listenerDetailsDrawer.tabs.blockedListeners'),
        count: blockedListTotal,
        disabled: blockedListTotal > 0 ? false : true,
      },
      {
        index: '3',
        title: `${translate('listenerDetailsDrawer.tabs.general')}`,
        disabled: false,
      },
    ],
    [translate, waitingList, blockedListTotal, spokeUsersMetaData],
  );

  const onTabChange = (e: React.SyntheticEvent<HTMLDivElement>) => {
    const { index } = e.currentTarget.dataset;
    if (index) {
      setTabIndex(+index);

      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: TABS[+index].title,
      });
    }
  };

  const clickOnStatisticBtn = () => {
    setIsShowStatistic(!isShowStatistic);
    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: translate('usersStatisticsDrawer.statisticBtn'),
    });
  };

  const renderActiveTabTitle = () => {
    if (TABS[tabIndex] && TABS[tabIndex].count !== 0) {
      return (
        <div className={css.tabTitle}>
          <p>{`${TABS[tabIndex].title} (${TABS[tabIndex].count})`}</p>
        </div>
      );
    }

    return null;
  };

  const showMoreBtnState = (st: boolean) => {
    setIsShowMoreBtn(st);
  };

  const clickOnLoadMoreButton = () => {
    switch (tabIndex) {
      case 0:
        dispatch(getRaiseHandMessages({ roomId, loadMore: true }));
        setIsShowMoreBtn(false);
        break;
      case 1:
        dispatch(getAllUsersMetadata({ roomId, next: spokeUsersMetaData.nextPage }));
        break;
      case 2:
        dispatch(getBlockedUsers({ roomId: `${roomId}.${PB_CHANNELS.BLOCKED}`, loadMore: true }));
        setIsShowMoreBtn(false);
        break;
      default:
        break;
    }
  };

  const renderLoadMoreButton = () => {
    if (tabIndex === 0 && waitingList.length < waitingListTotal) {
      return (
        <Button
          label={translate('loadMoreButton')}
          className={css.loadMoreButton}
          onClickHandler={clickOnLoadMoreButton}
        />
      );
    }

    if (tabIndex === 1 && spokeUsersMetaData.usersData.length < spokeUsersMetaData.totalCount) {
      return (
        <Button
          label={translate('loadMoreButton')}
          className={css.loadMoreButton}
          onClickHandler={clickOnLoadMoreButton}
        />
      );
    }

    if (tabIndex === 2 && blockedList.length < blockedListTotal) {
      return (
        <Button
          label={translate('loadMoreButton')}
          className={css.loadMoreButton}
          onClickHandler={clickOnLoadMoreButton}
        />
      );
    }
    return null;
  };

  const renderContent = () => {
    let renderList: Array<PubnubMsgsItem> | Array<UserMetadata> = [];

    if (tabIndex === 0) renderList = waitingList;
    if (tabIndex === 1) renderList = spokeUsersMetaData.usersData;
    if (tabIndex === 2) renderList = blockedList;

    if (tabIndex === 3) {
      return (
        <GeneralStatisticsList
          className={css.generalStatistic}
          totalListenersCount={totalListenersCount}
          connectedListenersCount={connectedListenersCount}
          pendingRequestsCount={pendingRequestsCount}
          userRole={userRole}
        />
      );
    }

    return (
      <>
        <VirtualUsersList renderList={renderList} showMoreBtnState={showMoreBtnState} tabIndex={tabIndex} />
        {isShowMoreBtn && renderLoadMoreButton()}
      </>
    );
  };

  return (
    <TransitionComponent visible={visible} type="transformRight">
      <div className={cn(css.drawer, css[`${className}`], isListenerTalking && css.isListenerTalking)}>
        <div className={css.line} onClick={handleClose} />
        <DrawerCloseBtn onClick={handleClose} />
        <div className={css.titleWrapper}>
          {isRoomFinished ? (
            <div className={css.title}>{translate('listenerDetailsDrawer.finishedRoomTitle')}</div>
          ) : (
            <div className={css.title}>
              {translate('listenerDetailsDrawer.title')} ({totalListenersCount})
            </div>
          )}
          <div className={cn(css.statisticBtn, isShowStatistic && css.active)} onClick={clickOnStatisticBtn}>
            {translate('usersStatisticsDrawer.statisticBtn')}
            <i></i>
          </div>
        </div>

        {isMobile && <WaiterSpeakContainer isInlineControls />}
        <div className={css.tabsContainer}>
          <div className={css.tabs}>
            {_.map(TABS, (t) => {
              return (
                <div
                  key={t.index}
                  className={cn(css.tab, +t.index == tabIndex && css.active, t.disabled && css.disabled)}
                  data-index={t.index}
                  onClick={onTabChange}
                >
                  {t.count !== undefined ? `${t.title} (${t.count})` : t.title}
                </div>
              );
            })}
          </div>
        </div>
        {!isMobile && <WaiterSpeakContainer isInlineControls />}
        {renderActiveTabTitle()}
        <div className={css.content}>{renderContent()}</div>
        <TransitionComponent visible={isShowStatistic} type="transformBottom">
          <div className={cn(css.statistic)}>
            <div className={css.statisticWrapper}>
              <GeneralStatisticsList
                className={css.generalStatistic}
                totalListenersCount={totalListenersCount}
                connectedListenersCount={connectedListenersCount}
                pendingRequestsCount={pendingRequestsCount}
                userRole={userRole}
              />
            </div>
          </div>
        </TransitionComponent>
      </div>
    </TransitionComponent>
  );
};

export default ListenersDetailsDrawer;
